/**
 * PlanTab — the six-month protocol: phases, the dated checkpoints that gate them, the
 * running experiment, and the setup that all of it reads from.
 *
 * THE GATE IS THE REASON THIS SCREEN EXISTS. Phase 02 is maximal-effort interval work in a
 * hypertensive person, and it is gated on a doctor's clearance. A blocked phase is rendered
 * as blocked and NAMES WHAT IS BLOCKING IT — it is never shown as merely "upcoming", because
 * the whole point of making the gate a foreign key was that a UI could not quietly ignore it.
 *
 * TONE, same as everywhere else in this module: it records and it never advises. The
 * experiment readout states two means, two spreads and a delta. It offers no verdict, no
 * significance test and no "this suggests" — the conclusion field is Brian's to write.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type {
  WellnessCheckpoint,
  WellnessExperiment,
  WellnessExperimentResult,
  WellnessMedication,
  WellnessPhasePlan,
  WellnessPhaseState,
  WellnessTarget,
} from '../../shared/types';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows } from '../home/ui';
import { gramsToLb, lbToGrams, shortDay } from './format';

export default function PlanTab({ onChanged }: { onChanged: () => void }) {
  const [plan, setPlan] = useState<WellnessPhasePlan | null>(null);
  const [checkpoints, setCheckpoints] = useState<WellnessCheckpoint[]>([]);
  const [experiments, setExperiments] = useState<WellnessExperiment[]>([]);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [p, c, e] = await Promise.all([
        api.get<WellnessPhasePlan>('/wellness/phases'),
        api.get<WellnessCheckpoint[]>('/wellness/checkpoints'),
        api.get<WellnessExperiment[]>('/wellness/experiments'),
      ]);
      setPlan(p);
      setCheckpoints(c);
      setExperiments(e);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = () => {
    void load();
    onChanged();
  };

  if (error)
    return (
      <Card className="p-5">
        <ErrorState what="your plan" onRetry={() => void load()} />
      </Card>
    );
  if (!plan)
    return (
      <Card className="p-5">
        <LoadingRows rows={6} />
      </Card>
    );

  return (
    <div className="space-y-4">
      <Phases plan={plan} />
      <Checkpoints checkpoints={checkpoints} onChanged={refresh} />
      {experiments.map((e) => (
        <ExperimentCard key={e.id} experiment={e} />
      ))}
      <Setup onChanged={refresh} />
    </div>
  );
}

// ---------------------------------------------------------------- phases

function Phases({ plan }: { plan: WellnessPhasePlan }) {
  if (!plan.phases.length)
    return (
      <Card className="p-5">
        <CardTitle icon="calendar">Phases</CardTitle>
        <div className="mt-4">
          <Empty message="No phases set up yet." />
        </div>
      </Card>
    );

  return (
    <Card className="p-5">
      <CardTitle icon="calendar">Phases</CardTitle>
      {!plan.current && (
        <p className="mt-3 text-sm text-ink-soft">
          The first phase starts {shortDay(plan.phases[0].phase.starts_on)}.
        </p>
      )}
      <ul className="mt-4 space-y-2">
        {plan.phases.map((s) => (
          <PhaseRow key={s.phase.id} state={s} />
        ))}
      </ul>
    </Card>
  );
}

function PhaseRow({ state }: { state: WellnessPhaseState }) {
  const { phase, is_current, blocked_by } = state;
  const blocked = blocked_by.length > 0;

  return (
    <li
      className={`rounded-xl border px-3 py-2.5 ${
        is_current ? 'border-sage bg-sage-soft' : 'border-border bg-canvas'
      }`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-ink">
          {String(phase.phase_number).padStart(2, '0')} {phase.label}
          {is_current && <span className="ml-2 text-xs font-normal text-sage">Current</span>}
        </span>
        <span className="shrink-0 text-xs text-ink-soft">
          {shortDay(phase.starts_on)}
          {phase.ends_on ? ` – ${shortDay(phase.ends_on)}` : ''}
        </span>
      </div>
      {phase.focus && <p className="mt-0.5 text-sm text-ink-soft">{phase.focus}</p>}

      {/* A blocked phase says what is blocking it, by name and date. Never just "upcoming". */}
      {blocked && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-honey-soft px-2.5 py-1.5 text-xs text-honey">
          <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            Not startable yet — waiting on{' '}
            {blocked_by.map((b, i) => (
              <span key={b.id}>
                {i > 0 && ', '}
                <strong className="font-semibold">{b.label}</strong> ({shortDay(b.due_on)})
              </span>
            ))}
            .
          </span>
        </p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- checkpoints

function Checkpoints({
  checkpoints,
  onChanged,
}: {
  checkpoints: WellnessCheckpoint[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);

  const setStatus = async (id: number, status: string) => {
    setBusy(id);
    try {
      await api.post(`/wellness/checkpoints/${id}/complete`, { status });
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  if (!checkpoints.length)
    return (
      <Card className="p-5">
        <CardTitle icon="pin">Checkpoints</CardTitle>
        <div className="mt-4">
          <Empty message="No checkpoints yet." />
        </div>
      </Card>
    );

  const pending = checkpoints.filter((c) => c.status === 'pending');
  const done = checkpoints.filter((c) => c.status !== 'pending');

  return (
    <Card className="p-5">
      <CardTitle icon="pin">Checkpoints</CardTitle>
      <ul className="mt-4 space-y-1.5">
        {pending.map((c) => (
          <li
            key={c.id}
            className="flex items-center gap-3 rounded-xl border border-border bg-canvas px-3 py-2.5"
          >
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-ink">
                {c.label}
                {/* A gate is labelled as one, so its weight is visible before it is ticked. */}
                {c.gates_phase_id && (
                  <span className="ml-2 rounded bg-honey-soft px-1.5 py-0.5 text-xs text-honey">
                    gates a phase
                  </span>
                )}
              </span>
              <span className="block text-xs text-ink-soft">
                {shortDay(c.due_on)} · {c.category}
              </span>
              {c.detail && <span className="mt-0.5 block text-xs text-ink-soft">{c.detail}</span>}
            </span>
            <button
              type="button"
              disabled={busy === c.id}
              onClick={() => setStatus(c.id, 'done')}
              className="min-h-11 shrink-0 rounded-full border border-sage bg-surface px-3 text-xs font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              Done
            </button>
          </li>
        ))}
      </ul>

      {done.length > 0 && (
        <ul className="mt-3 space-y-1">
          {done.map((c) => (
            <li key={c.id} className="flex items-center gap-3 px-3 text-sm">
              <span className="min-w-0 flex-1 text-ink-soft">
                <span className="line-through">{c.label}</span>
                {c.completed_on && (
                  <span className="ml-2 text-xs">{shortDay(c.completed_on)}</span>
                )}
              </span>
              {/* Reversible on purpose: this is what unlocks a gated phase. */}
              <button
                type="button"
                disabled={busy === c.id}
                onClick={() => setStatus(c.id, 'pending')}
                className="min-h-11 shrink-0 text-xs text-ink-faint hover:text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                Undo
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- experiment

function ExperimentCard({ experiment }: { experiment: WellnessExperiment }) {
  const [result, setResult] = useState<WellnessExperimentResult | null>(null);

  useEffect(() => {
    api
      .get<WellnessExperimentResult>(`/wellness/experiments/${experiment.id}/result`)
      .then(setResult)
      .catch(() => setResult(null));
  }, [experiment.id]);

  return (
    <Card className="p-5">
      <CardTitle icon="pulse">{experiment.label}</CardTitle>
      {experiment.hypothesis && (
        <p className="mt-2 text-sm text-ink-soft">{experiment.hypothesis}</p>
      )}
      <p className="mt-1 text-xs text-ink-soft">
        Baseline {shortDay(experiment.baseline_start)} – {shortDay(experiment.baseline_end)} ·
        Intervention {shortDay(experiment.intervention_start)} –{' '}
        {shortDay(experiment.intervention_end)}
      </p>
      {experiment.intervention_detail && (
        <p className="mt-1 text-xs text-ink-soft">{experiment.intervention_detail}</p>
      )}

      {!result ? (
        <div className="mt-4">
          <LoadingRows rows={2} />
        </div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-ink-soft uppercase">
                  <th className="pb-1 text-left font-medium"> </th>
                  <th className="pb-1 text-right font-medium">Baseline</th>
                  <th className="pb-1 text-right font-medium">Intervention</th>
                  <th className="pb-1 text-right font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {result.components.map((c) => (
                  <tr key={c.key} className="border-t border-border">
                    <td className="py-2 text-ink">{c.label}</td>
                    <td className="py-2 text-right text-ink">
                      <Stat n={c.baseline.n} mean={c.baseline.mean} sd={c.baseline.sd} />
                    </td>
                    <td className="py-2 text-right text-ink">
                      <Stat
                        n={c.intervention.n}
                        mean={c.intervention.mean}
                        sd={c.intervention.sd}
                      />
                    </td>
                    <td className="py-2 text-right font-medium text-ink">
                      {c.delta === null ? (
                        <span className="text-ink-faint">—</span>
                      ) : (
                        `${c.delta > 0 ? '+' : ''}${c.delta}`
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* States the data situation. Still no interpretation of the numbers themselves. */}
          <p className="mt-3 text-xs text-ink-soft">
            {result.sufficient_data
              ? `Both windows have at least ${result.min_readings} readings.`
              : `Not enough readings yet — this needs ${result.min_readings} in each window before the comparison means much.`}
          </p>
        </>
      )}
    </Card>
  );
}

function Stat({ n, mean, sd }: { n: number; mean: number | null; sd: number | null }) {
  if (mean === null) return <span className="text-ink-faint">no readings</span>;
  return (
    <span>
      {mean}
      {sd !== null && <span className="text-ink-soft"> ±{sd}</span>}
      <span className="ml-1 text-xs text-ink-faint">n={n}</span>
    </span>
  );
}

// ---------------------------------------------------------------- setup

/**
 * Medications and targets, editable here rather than by SQL.
 *
 * This exists because the seed deliberately leaves the lisinopril dose NULL — the PRD never
 * stated it and a dose is not something to infer — and "edit the database" is not an answer
 * for the one field a doctor will ask about.
 */
function Setup({ onChanged }: { onChanged: () => void }) {
  const [meds, setMeds] = useState<WellnessMedication[]>([]);
  const [targets, setTargets] = useState<WellnessTarget[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [m, t] = await Promise.all([
      api.get<WellnessMedication[]>('/wellness/medications'),
      api.get<WellnessTarget[]>('/wellness/targets'),
    ]);
    setMeds(m);
    setTargets(t);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveMed = async (id: number, patch: Partial<WellnessMedication>) => {
    setSaving(true);
    try {
      await api.put(`/wellness/medications/${id}`, patch);
      await load();
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  const saveTarget = async (t: WellnessTarget, value: string) => {
    setSaving(true);
    try {
      await api.put('/wellness/targets', {
        slug: t.slug,
        label: t.label,
        unit: t.unit,
        value,
      });
      await load();
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        <CardTitle icon="gear">Setup</CardTitle>
        <Icon
          name="arrow"
          className={`h-4 w-4 text-ink-faint transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {!open ? (
        <p className="mt-2 text-sm text-ink-soft">
          Medications, doses and targets. Nothing here is hardcoded — editing a target never
          needs a deploy.
        </p>
      ) : (
        <div className="mt-4 space-y-5">
          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-soft uppercase">
              Medications
            </h3>
            <ul className="space-y-1.5">
              {meds.map((m) => (
                <li key={m.id} className="rounded-xl border border-border bg-canvas px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium text-ink">{m.name}</span>
                    <span className="text-xs text-ink-soft capitalize">
                      {m.category} · {m.schedule.replace('_', ' ')}
                    </span>
                  </div>
                  <InlineEdit
                    label="Dose"
                    value={m.dose}
                    placeholder="e.g. 10 mg"
                    saving={saving}
                    onSave={(v) => saveMed(m.id, { dose: v || null })}
                  />
                  {!m.dose && (
                    <p className="mt-1 text-xs text-honey">
                      No dose recorded — worth setting before the next appointment.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-soft uppercase">
              Targets
            </h3>
            <ul className="space-y-1.5">
              {targets.map((t) => (
                <li key={t.id} className="rounded-xl border border-border bg-canvas px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium text-ink">{t.label}</span>
                    <span className="text-xs text-ink-soft">{t.unit}</span>
                  </div>
                  {/* Weight is stored in grams; it is edited in pounds and converted here so
                      nobody has to think in grams to change a goal. */}
                  {t.slug === 'weight_g' ? (
                    <InlineEdit
                      label="Goal (lb)"
                      value={String(Math.round(gramsToLb(Number(t.value)) * 10) / 10)}
                      saving={saving}
                      onSave={(v) => saveTarget(t, String(lbToGrams(Number(v))))}
                    />
                  ) : (
                    <InlineEdit
                      label="Value"
                      value={t.value}
                      saving={saving}
                      onSave={(v) => saveTarget(t, v)}
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </Card>
  );
}

function InlineEdit({
  label,
  value,
  placeholder,
  saving,
  onSave,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  saving: boolean;
  onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const dirty = draft !== (value ?? '');

  return (
    <div className="mt-2 flex items-end gap-2">
      <label className="min-w-0 flex-1">
        <span className="mb-1 block text-xs text-ink-soft">{label}</span>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          className="min-h-11 w-full rounded-xl border border-border bg-surface px-3 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        />
      </label>
      <button
        type="button"
        disabled={!dirty || saving}
        onClick={() => onSave(draft.trim())}
        className="min-h-11 shrink-0 rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage disabled:border-border disabled:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        Save
      </button>
    </div>
  );
}
